// Bedragen en datums in de schrijfwijze van de taal van de widget (Europa, Martijn 15-10-2026).
//
// Waarom: format_Number deed toLocaleString('en-GB') en verving daarna de eerste punt door een komma. Bij een bedrag onder
// de 1000 ging dat goed ("12,50"), maar 1234,5 werd "1,234,50": de punt die vervangen werd was de decimaalpunt, de komma
// van de duizendtallen bleef staan. En het euroteken stond overal vast vooraan, ook voor een Franse of Duitse gast, die
// "12,50 €" schrijft. Nu volgt alles de taal van de widget via Intl (zit in elke browser).

import { huidigeTaal } from './woorden';

// De taal van de widget als cultuur. Engels is en-GB (24-uursklok, dag-maand-jaar), niet Amerikaans.
const CULTUREN = {
    nl: 'nl-NL', en: 'en-GB', de: 'de-DE', fr: 'fr-FR', es: 'es-ES', it: 'it-IT', pt: 'pt-PT',
    da: 'da-DK', sv: 'sv-SE', no: 'nb-NO', fi: 'fi-FI', pl: 'pl-PL', tr: 'tr-TR',
};

/** De cultuur (nl-NL, de-DE, ...) bij een taalcode; zonder taal die van de widget. */
export function cultuur(taal) {
    const t = String(taal || huidigeTaal() || 'nl').toLowerCase().split(/[-_]/)[0];
    return CULTUREN[t] || 'nl-NL';
}

function getal(waarde) {
    const n = typeof waarde === 'string' ? parseFloat(waarde) : Number(waarde);
    return Number.isFinite(n) ? n : 0;
}

/** Een bedrag zonder valutateken, met twee decimalen: "1.234,50" (nl, de), "1,234.50" (en), "1 234,50" (fr). */
export function bedragTekst(waarde, taal) {
    return getal(waarde).toLocaleString(cultuur(taal), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// De munt van de zaak (lib/winkelvaluta.js zet hem bij het opstarten; zonder antwoord euro). Zweden/Polen, 17-10-2026.
let winkelValuta = 'EUR';

/** Zet de munt van de zaak (ISO-code: EUR, SEK, PLN) als standaard voor geldTekst. */
export function zetWinkelValuta(code) {
    const c = String(code || '').trim().toUpperCase();
    winkelValuta = /^[A-Z]{3}$/.test(c) ? c : 'EUR';
}

/** De munt van de zaak (ISO-code). */
export function huidigeWinkelValuta() {
    return winkelValuta;
}

/**
 * Een bedrag met valutateken op de plek die bij de taal hoort: "€ 12,50" (nl), "12,50 €" (de, fr), "€12.50" (en), "120,00 kr" (sv).
 * Zonder munt: de munt van de zaak.
 */
export function geldTekst(waarde, valuta, taal) {
    const munt = valuta || winkelValuta || 'EUR';
    try {
        return new Intl.NumberFormat(cultuur(taal), { style: 'currency', currency: munt }).format(getal(waarde));
    } catch {
        return `${munt === 'EUR' ? '€' : munt} ${bedragTekst(waarde, taal)}`;
    }
}

/** Een tijd altijd op de 24-uursklok: "19:30". */
export function tijdTekst(datum, taal) {
    const d = datum instanceof Date ? datum : new Date(datum);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(cultuur(taal), { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d);
}

/** Een korte datum in de schrijfwijze van de taal: 09-10-2026 (nl), 09.10.2026 (de), 09/10/2026 (fr, en). */
export function datumKort(datum, taal) {
    const d = datum instanceof Date ? datum : new Date(datum);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(cultuur(taal), { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}
